if (!customElements.get('cart-function-panels')) {
  class CartFunctionPanels extends HTMLElement {
    constructor() {
      super();
      this._debounceId = null;
      this._activeContent = null;
      this._shouldFocusNote = false;
      this._onInput = this._onInput.bind(this);
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onDocumentPointerDown = this._onDocumentPointerDown.bind(this);
      this._onPanelTransitionEnd = this._onPanelTransitionEnd.bind(this);
    }

    connectedCallback() {
      this._textarea = this.querySelector('textarea[name="note"]');
      this._panel = this.querySelector('[data-cart-function-panel-wrapper]');
      this._triggers = Array.from(
        this.closest('[data-cart-footer]')?.querySelectorAll('[data-cart-function-trigger]') ?? []
      );
      this._contentPanels = Array.from(this.querySelectorAll('[data-cart-function-content]'));

      for (const panel of this._contentPanels) {
        panel.hidden = false;
      }

      if (this._textarea) {
        this._textarea.addEventListener('input', this._onInput);
      }

      if (this._panel) {
        this._panel.addEventListener('transitionend', this._onPanelTransitionEnd);
      }

      for (const trigger of this._triggers) {
        trigger.addEventListener('click', this._onTriggerClick);
      }

      document.addEventListener('pointerdown', this._onDocumentPointerDown);
      this._setActiveContent(this.dataset.activePanel);
      this._syncToggleState({ immediate: true });
    }

    disconnectedCallback() {
      if (this._textarea) {
        this._textarea.removeEventListener('input', this._onInput);
      }

      if (this._panel) {
        this._panel.removeEventListener('transitionend', this._onPanelTransitionEnd);
      }

      for (const trigger of this._triggers ?? []) {
        trigger.removeEventListener('click', this._onTriggerClick);
      }

      document.removeEventListener('pointerdown', this._onDocumentPointerDown);

      if (this._debounceId) {
        clearTimeout(this._debounceId);
        this._debounceId = null;
      }
    }

    _onInput(event) {
      if (!(event.target instanceof HTMLTextAreaElement)) return;

      if (this._debounceId) {
        clearTimeout(this._debounceId);
      }

      const note = event.target.value;

      this._debounceId = window.setTimeout(async () => {
        try {
          await fetch(Theme.routes.cart_update_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ note }),
          });
        } catch (error) {}
      }, 200);
    }

    _onTriggerClick(event) {
      const trigger = event.currentTarget;
      if (!(trigger instanceof HTMLButtonElement)) return;

      const panelName = trigger.dataset.cartFunctionPanel;
      if (typeof panelName !== 'string' || panelName === '') return;

      if (this.hasAttribute('data-open') && this.dataset.activePanel === panelName) {
        this._closePanel();
        return;
      }

      this._openPanel(panelName);
    }

    _onDocumentPointerDown(event) {
      if (!this.hasAttribute('data-open')) return;
      if (!(event.target instanceof Node)) return;
      if (this.contains(event.target)) return;

      const footer = this.closest('[data-cart-footer]');
      if (footer instanceof HTMLElement && footer.contains(event.target)) return;

      this._closePanel();
    }

    _closePanel() {
      if (this._panel instanceof HTMLElement) {
        const currentHeight = this._panel.getBoundingClientRect().height;
        this._panel.style.height = `${currentHeight}px`;
      }

      this._shouldFocusNote = false;
      this.removeAttribute('data-open');
      delete this.dataset.activePanel;
      this._setActiveContent('');
      this._syncToggleState();
      this._panel?.getBoundingClientRect();

      requestAnimationFrame(() => {
        if (this._panel instanceof HTMLElement) {
          this._panel.style.height = '0px';
        }
      });
    }

    _openPanel(panelName) {
      const nextPanel = this._getContentPanel(panelName);
      if (!(nextPanel instanceof HTMLElement) || !(this._panel instanceof HTMLElement)) return;

      const previousHeight = this._panel.getBoundingClientRect().height;
      const wasOpen = this.hasAttribute('data-open');
      const startHeight = wasOpen ? previousHeight : 0;
      this._panel.style.height = `${startHeight}px`;

      this.dataset.activePanel = panelName;
      this.setAttribute('data-open', 'true');
      this._shouldFocusNote = panelName === 'note';
      this._setActiveContent(panelName);
      this._syncToggleState();
      const nextHeight = this._measureContentHeight(nextPanel);
      this._panel.getBoundingClientRect();

      requestAnimationFrame(() => {
        this._panel.style.height = `${nextHeight}px`;

        if ((!wasOpen || Math.abs(startHeight - nextHeight) < 1) && panelName === 'note') {
          this._focusNoteIfNeeded();
        }
      });
    }

    _syncToggleState({ immediate = false } = {}) {
      const isOpen = this.hasAttribute('data-open');
      const activePanel = this.dataset.activePanel;
      const activeContent = this._activeContent;

      if (this._panel instanceof HTMLElement) {
        if (isOpen) {
          this._panel.setAttribute('data-open', 'true');
        } else {
          this._panel.removeAttribute('data-open');
        }

        if (immediate) {
          this._panel.style.height =
            isOpen && activeContent instanceof HTMLElement ? `${activeContent.scrollHeight}px` : '0px';
        }
      }

      for (const trigger of this._triggers ?? []) {
        if (!(trigger instanceof HTMLButtonElement)) continue;
        const isActive = isOpen && trigger.dataset.cartFunctionPanel === activePanel;
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }
    }

    _getContentPanel(panelName) {
      if (typeof panelName !== 'string') return null;

      return this._contentPanels.find((panel) => {
        return panel instanceof HTMLElement && panel.dataset.cartFunctionContent === panelName;
      });
    }

    _measureContentHeight(panel) {
      if (!(panel instanceof HTMLElement)) return 0;

      return panel.scrollHeight;
    }

    _setActiveContent(panelName) {
      const activeContent = this._getContentPanel(panelName);

      for (const panel of this._contentPanels ?? []) {
        if (!(panel instanceof HTMLElement)) continue;

        if (panel === activeContent) {
          panel.setAttribute('data-active', 'true');
        } else {
          panel.removeAttribute('data-active');
        }
      }

      this._activeContent = activeContent instanceof HTMLElement ? activeContent : null;
    }

    _focusNoteIfNeeded() {
      if (!this._shouldFocusNote) return;
      if (!(this._textarea instanceof HTMLTextAreaElement)) return;

      this._textarea.focus({ preventScroll: true });
      this._shouldFocusNote = false;
    }

    _onPanelTransitionEnd(event) {
      if (!(this._panel instanceof HTMLElement) || event.target !== this._panel || event.propertyName !== 'height') {
        return;
      }

      const isOpen = this.hasAttribute('data-open');

      if (isOpen && this._activeContent instanceof HTMLElement) {
        this._panel.style.height = 'auto';
        this._focusNoteIfNeeded();
        return;
      }

      this._panel.style.height = '0px';
    }
  }

  customElements.define('cart-function-panels', CartFunctionPanels);
}
